import { Socket } from 'socket.io-client';
import { realtimeSocketHub } from './realtimeSocketHub';

export type RepairLockSocketPayload = {
  repairId: string;
  editingBy: unknown | null;
};

export type CustomerPaidSocketPayload = {
  repairId?: string;
  callNumber?: string;
  amount?: number;
  paidAt?: string;
  source?: string;
};

class AppRealtimeService {
  private attached = false;
  private detachSocketConsumer: (() => void) | null = null;

  async connect(userId: string): Promise<void> {
    this.ensureListenersRegistered();
    await realtimeSocketHub.ensureConnected(userId);
  }

  private ensureListenersRegistered(): void {
    if (this.attached) return;
    this.attached = true;
    // listeners של שינויי קריאות/דשבורד — מוצמדים לכל socket חדש שה-Hub יוצר.
    this.detachSocketConsumer = realtimeSocketHub.onSocket((socket: Socket) => {
      socket.on('repairs:changed', () => {
        window.dispatchEvent(new CustomEvent('repair-mutated', { detail: { source: 'realtime' } }));
      });
      socket.on('dashboard:stale', () => {
        window.dispatchEvent(new CustomEvent('dashboard-stale', { detail: { source: 'realtime' } }));
      });
      socket.on('payment:customer-paid', (payload: CustomerPaidSocketPayload) => {
        window.dispatchEvent(
          new CustomEvent('repair-payment-customer-paid', {
            detail: payload || {},
          }),
        );
        window.dispatchEvent(new CustomEvent('repair-mutated', { detail: { source: 'customer-payment' } }));
      });
      socket.on('knowledge-base:news-changed', (payload: { id?: string }) => {
        window.dispatchEvent(new CustomEvent('knowledge-base-news-changed', { detail: payload || {} }));
      });
    });
  }

  isConnected(): boolean {
    return realtimeSocketHub.isConnected();
  }

  /** רישום socket כבעל נעילה — שחרור אוטומטי בניתוק */
  holdRepairEditLock(repairId: string): void {
    const id = String(repairId || '').trim();
    const socket = realtimeSocketHub.getSocket();
    if (!id || !socket?.connected) return;
    socket.emit('repair-lock:hold', { repairId: id });
  }

  /**
   * האזנה מיידית לשינויי נעילה (במקום polling / heartbeat).
   * @returns unsubscribe
   */
  watchRepairEditLock(
    repairId: string,
    onUpdate: (editingBy: unknown | null) => void,
  ): () => void {
    const id = String(repairId || '').trim();
    const socket = realtimeSocketHub.getSocket();
    if (!id || !socket?.connected) return () => {};

    const handler = (payload: RepairLockSocketPayload) => {
      if (String(payload?.repairId || '').trim() !== id) return;
      onUpdate(payload?.editingBy ?? null);
    };

    socket.on('repair-lock:updated', handler);
    socket.emit('repair-lock:watch', { repairId: id });

    return () => {
      const current = realtimeSocketHub.getSocket();
      current?.off('repair-lock:updated', handler);
      if (current?.connected) {
        current.emit('repair-lock:unwatch', { repairId: id });
      }
    };
  }

  disconnect(): void {
    this.detachSocketConsumer?.();
    this.detachSocketConsumer = null;
    this.attached = false;
    realtimeSocketHub.disconnect();
  }
}

export const appRealtimeService = new AppRealtimeService();
